<script lang="ts" setup>
import CategoriesList from '~/widgets/catalog/CategoriesList.vue'
import CatalogInfo from '~/widgets/catalog/CatalogInfo.vue'
import SeoProductDescription from '~/entities/products/SeoProductDescription.vue'
import useMainStore from '~/shared/store'
import useCatalogStore from '~/shared/store/modules/catalog'
import serverConfig from '~/server/config'
import customFetch from '~/composables/custom-fetch'
import type {ICatalogFilterValue} from '~/shared/interfaces/catalog'
import type {
  ICatalogFilter,
  ICatalogFiltersQuery,
  ICatalogFiltersSelected,
  ICatalogResult,
  ISelectedFilterItem,
} from '~/shared/interfaces/catalog'
import type {IBreadcrumbs} from '~/shared/interfaces/ui/breadcrumbs'
import {useRequestURL} from "#app";

const catalogStore = useCatalogStore()
const mainStore = useMainStore()

const route = useRoute()
const router = useRouter()
const path = useRequestURL().pathname
const {catalog} = serverConfig

definePageMeta({
  validate: async (route) => {
    const data = await $fetch(`${import.meta.env.VITE_BACKEND_URL}${route.fullPath}`)
    return !!data;
  },
  layout: 'catalog',
})

const filterSeparator = ';'
const filterValueSeparator = '!'

let watchSelectedList = false
catalogStore.setSlug(route.params.slug as string[])

watch(
  () => catalogStore.selectedFilters,
  () => {
    if (mainStore.allContentLoaded && !catalogStore.currentCategories) {
      if (watchSelectedList) {
        getProductsInfo(false)
      } else {
        watchSelectedList = true
      }
    }
  },
  {
    deep: true,
  }
)

function getSeoRequestUrl(url: string) {
  return url.replace('/catalog/', '').split('?')[0]
}

async function getProductsInfo(checkUri: boolean) {
  mainStore.setContentLoadingStatus(false)

  const query = getSelectedFilters(checkUri)

  let productInfo: ICatalogResult | null = null

  if (!watchSelectedList) {
    const {data, status} = await customFetch<ICatalogResult>(
      catalog.getCatalogItems({
        seo: path,
        query,
      })
    )
    if (status === 404) {
      throw createError({ statusCode: 404, statusMessage: 'Page Not Found', fatal: true })
    }

    productInfo = data ? data : null
  }

  if (!productInfo) {
    if (catalogStore.uuidSlug) {
      const {data, status} = await customFetch<ICatalogResult>(
        catalog.getCategories({
          slug: catalogStore.uuidSlug,
          query,
        })
      )

      productInfo = data ? data : null
    }
  }

  if (
    (productInfo && productInfo.brands.length > 1) ||
    (productInfo && productInfo.brands.length > 1) ||
    (productInfo && productInfo.filters.length)
  ) {
    productInfo.filters.unshift({
      id: 'brands',
      title: 'Бренд',
      unit: null,
      sortedByAlphabetValues: productInfo.brands.reduce<{
        [key: string]: ICatalogFilterValue[]
      }>((acc, brand) => {
        const {brandName, brandId, isActive, count} = brand
        const title = brandName.toUpperCase().slice(0, 1)

        if (!acc[title]) {
          acc[title] = []
        }

        acc[title].push({
          title: brandName,
          value: String(brandId),
          active: isActive,
          count,
        })

        return acc
      }, {}),
      values: [
        ...productInfo.brands.map<ICatalogFilterValue>((brand) => {
          const {brandName, brandId, isActive, count} = brand
          return {
            title: brandName,
            value: String(brandId),
            active: isActive,
            count,
          }
        }),
      ],
    })
  }

  if (productInfo) {
    if (!watchSelectedList) {
      catalogStore.selectedFilters.page = productInfo.catalog.currentPage
      setInitialSelectedFilters(
        productInfo.filters,
        productInfo.catalog.currentPage
      )
    }

    catalogStore.setCatalogInfo(productInfo)

    if (productInfo.seo.url) {
      await router.replace(`/${productInfo.seo.url}`)
    } else {
      await router.replace({
        path: getOriginalUrl(catalogStore.catalogBreadcrumbs),
        query: {
          page:
            catalogStore.selectedFilters.page > 1
              ? catalogStore.selectedFilters.page
              : undefined,
          brands: catalogStore.selectedFilters.brands.map(({value}) => value),
          filters:
            getFiltersUriString(
              catalogStore.selectedFilters.filters.map(
                ({filterId, values}) => {
                  return {
                    filterId,
                    values: values.map(({value}) => value),
                  }
                }
              )
            ) || undefined,
        },
      })
    }
    watchSelectedList = true
  }

  mainStore.setContentLoadingStatus(true)

  if (!productInfo) {
    throw createError({
      statusCode: 404,
      statusMessage: 'Page Not Found',
      fatal: true,
    })
  }
}

function setInitialSelectedFilters(filters: ICatalogFilter[], page: number) {
  if (filters.length) {
    const filtersForReplace: ICatalogFiltersSelected =
      filters.reduce<ICatalogFiltersSelected>(
        (acc, {id, title, values}) => {
          const selectedValuesList = values.reduce<ISelectedFilterItem[]>(
            (acc, {value, title, active}) => {
              if (active) {
                acc.push({
                  name: title || value,
                  value,
                })
              }

              return acc
            },
            []
          )

          if (selectedValuesList.length) {
            if (id == 'brands') {
              acc.brands = selectedValuesList
            } else {
              acc.filters.push({
                filterId: id,
                filterName: title,
                values: selectedValuesList,
              })
            }
          }

          return acc
        },
        {
          filters: [],
          brands: [],
          sorting: 'popular',
          page: 1,
        }
      )

    filtersForReplace.page = page

    catalogStore.replaceInitialSelectedList(filtersForReplace)
  }
}

function getSelectedFilters(checkUri: boolean): ICatalogFiltersQuery {
  const {filters, brands, sorting, page} = catalogStore.selectedFilters
  const result: ICatalogFiltersQuery = {
    page: checkUri ? 1 : page,
    sorting: sorting,
    filters: [],
    brands: [],
  }

  if (filters.length) {
    result.filters = filters.map(({filterId, values}) => {
      return {
        filterId,
        values: values.map(({value}) => value),
      }
    })
  }

  if (brands.length) {
    result.brands = brands.map((brand) => brand.value)
  }

  if (checkUri) {
    const {
      filters: filtersQuery,
      brands: brandsQuery,
      page: pageQuery,
    } = route.query

    if (brandsQuery?.length) {
      result.brands = (
        typeof brandsQuery == 'string' ? [brandsQuery] : brandsQuery
      ) as string[]
    }

    if (filtersQuery) {
      result.filters = getFiltersFromUriString(filtersQuery as string)
    }

    if (pageQuery) {
      result.page = Number(pageQuery)
    }
  }

  return result
}

function getOriginalUrl(breadcrumbs: IBreadcrumbs[]) {
  return breadcrumbs.reduce(
    (acc, crumb) => (acc += `/${crumb.slug}`),
    '/catalog'
  )
}

function getFiltersFromUriString(filters: string) {
  const filtersArr = filters.split(filterValueSeparator)
  const result: ICatalogFiltersQuery['filters'] = []
  let index = 0

  while (filtersArr.length) {
    const filterId = filtersArr[index]
    const filterValues = filtersArr[index + 1]

    result.push({
      filterId,
      values: filterValues.split(filterSeparator),
    })

    filtersArr.splice(0, 2)
  }

  return result
}

function getFiltersUriString(filters: ICatalogFiltersQuery['filters']) {
  const filtersString = filters.reduce((acc, filter, i) => {
    acc +=
      (i > 0 ? filterValueSeparator : '') +
      filter.filterId +
      filterValueSeparator
    filter.values.forEach((filterValue, i) => {
      acc +=
        filter.values.length - 1 > i
          ? filterValue + filterSeparator
          : filterValue
    })
    return acc
  }, '')

  return filtersString
}

if (process.client && !catalogStore?.currentCategories) {
  catalogStore.catalog = null
  await nextTick(async () => {
    await getProductsInfo(true)
  })
}

</script>

<template>
  <div class="catalog">
    <CategoriesList
      v-if="catalogStore.currentCategories"
      :categories="catalogStore.currentCategories"
    />
    <CatalogInfo
      v-if="!catalogStore.currentCategories && catalogStore.catalog"
    />
    <SeoProductDescription
      v-if="
        (catalogStore.catalog?.currentPage === 1 ||
          !catalogStore.catalog?.currentPage) &&
        mainStore.seoAdditionalText
      "
      :additional-text="mainStore.seoAdditionalText"
    />
  </div>
</template>

<style lang="scss" scoped></style>
